import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject } from 'rxjs';

import { TwcPopupOptions, TwcPopupService } from '@topcon/twc-angular';

import { LocalStorageService } from '@mirage-projects/cw-commons';

import { MainNotificationDialogComponent } from '../../components/main-notification-dialog/main-notification-dialog.component';

interface MainNotification {
    id: string;
    type: string;
    timeOut: string;
    message: { en: string } & Record<string, string>;
    creationAt: string;
}

type MainNotificationResponse = { notifications: MainNotification[] };

@Injectable({ providedIn: 'root' })
export class MainNotificationService {
    private localStorageKey = 'mainNotifications';
    private currentNotification: null | MainNotification = null;

    constructor(
        private httpClient: HttpClient,
        private localStorageService: LocalStorageService,
        private popupService: TwcPopupService,
        private translateService: TranslateService
    ) {}

    async checkAndShowNotificationsFile(file: string): Promise<MainNotification[]> {
        const notifications = await this.checkNotificationsFile(file);
        const notificationsShown = [];

        if (notifications?.length) {
            const firstNotification = notifications[0];
            if (!this.isDismissed(firstNotification) && !this.isTimedOut(firstNotification)) {
                await this.showNotification(firstNotification);
                notificationsShown.push(firstNotification);
            }
        }
        return notificationsShown;
    }

    private async checkNotificationsFile(file: string): Promise<MainNotification[] | void> {
        if (!file) {
            return;
        }

        const response: MainNotificationResponse = await firstValueFrom(this.httpClient.get<MainNotificationResponse>(file));

        return Array.isArray(response?.notifications) ? response?.notifications : [];
    }

    private async showNotification(notification: MainNotification): Promise<boolean> {
        if (this.currentNotification) {
            return false;
        }

        this.currentNotification = notification;
        const language = this.translateService.currentLang.substring(0, 2);
        const message = notification?.message?.[language] ?? notification.message['en'];
        const close$ = new Subject<void>();
        const options: TwcPopupOptions = { params: { message, close$ } };
        const popup = this.popupService.open(MainNotificationDialogComponent, options);
        await firstValueFrom(close$);
        this.updateDismiss(notification);
        popup.close();
        return true;
    }

    private updateDismiss(notification: MainNotification): void {
        const dismissed = this.getDismissedIds();
        dismissed.push(notification.id);
        this.localStorageService.setItem(this.localStorageKey, dismissed);

        this.currentNotification = null;
    }

    private isDismissed(notification: MainNotification): boolean {
        const dismissed = this.getDismissedIds();
        return dismissed.includes(notification.id);
    }

    private getDismissedIds(): string[] {
        const jsonDismissed = this.localStorageService.getItem(this.localStorageKey) ?? '[]';
        const dismissed: string[] = JSON.parse(jsonDismissed);
        return dismissed;
    }

    private isTimedOut(notification: MainNotification): boolean {
        if (!notification.timeOut) {
            return false;
        }

        const now = new Date();
        const timeOut = new Date(notification.timeOut);
        return now.getTime() > timeOut.getTime();
    }
}
